import React, { useCallback, useMemo, useRef, useState } from 'react'
import { Grid } from '@material-ui/core'
import { connections } from '../../../../api/integrations'
import { decipherError } from '../../../../utils/decipherError'
import { ALERT_SEVERITY, ALERT_VARIANT } from '../../../../constants'
import Loading from '../../../molecules/Loading'
import SydButton from '../../../commonDesign/Button'
import SydModal from '../../../commonDesign/SydModal'
import SydModalActions from '../../../commonDesign/SydModal/SydModalActions'
import Alert from '../../../atoms/Alert'
import AuthDialog from './AuthDialog'

// Inline overrides until opal exposes equivalent utilities.
const DOMAIN_CARD_STYLE = {
  border: 'var(--border-neutral-thin)',
  borderRadius: 'var(--border-radius-sm)'
}

function statusFor (token) {
  if (!token) {
    return { className: 'font-size-xs text-neutral-400', style: undefined, label: 'Not connected' }
  }
  if (token.requiresReauth) {
    return {
      className: 'font-size-xs font-bold',
      style: { color: 'var(--color-danger-500)' },
      label: 'Connection error — reconnect required'
    }
  }
  if (token.linked) {
    return {
      className: 'font-size-xs',
      style: { color: 'var(--color-success-500)' },
      label: 'Connected'
    }
  }
  return {
    className: 'font-size-xs',
    style: { color: 'var(--color-warning-500)' },
    label: 'Connected (Unlinked)'
  }
}

function ConnectedIntegrations () {
  const authDialogRef = useRef(null)
  const [domainMessages, setDomainMessages] = useState({})
  const [disconnectTarget, setDisconnectTarget] = useState(null)

  const {
    data: exposedData,
    isLoading: exposedLoading,
    isError: exposedIsError,
    error: exposedError
  } = connections.useListExposedConnections()
  const {
    data: tokensData,
    isLoading: tokensLoading,
    isError: tokensIsError,
    error: tokensError
  } = connections.useListUserConnectionTokens(null)
  const { mutateAsync: revokeToken, isLoading: revoking } = connections.useRevokeUserConnectionToken()

  const domainGroups = useMemo(() => {
    const exposedConnections = exposedData?.connections || []
    const userTokens = tokensData?.tokens || []

    const domains = new Map()

    for (const conn of exposedConnections) {
      if (!domains.has(conn.domain)) {
        domains.set(conn.domain, {
          domain: conn.domain,
          domainName: conn.domainName || conn.domain,
          authInputs: conn.authInputs,
          token: null
        })
      }
    }

    for (const token of userTokens) {
      if (domains.has(token.domain)) {
        const group = domains.get(token.domain)
        group.token = token
        if (!group.domainName || group.domainName === group.domain) {
          group.domainName = token.domainName || group.domainName
        }
      } else {
        domains.set(token.domain, {
          domain: token.domain,
          domainName: token.domainName || token.domain,
          authInputs: null,
          token
        })
      }
    }

    return Array.from(domains.values())
  }, [exposedData, tokensData])

  const setDomainMessage = useCallback((domain, message) => {
    setDomainMessages(prev => {
      if (message == null) {
        if (!(domain in prev)) return prev
        const next = { ...prev }
        delete next[domain]
        return next
      }
      return { ...prev, [domain]: message }
    })
  }, [])

  const handleConnect = useCallback((domain, authInputs, domainName) => {
    setDomainMessage(domain, null)
    authDialogRef.current?.open(domain, authInputs, { domainName })
  }, [setDomainMessage])

  const handleAuthComplete = useCallback((result) => {
    const { domain, matched } = result || {}
    if (!domain) return
    if (!matched) {
      setDomainMessage(domain, {
        severity: ALERT_SEVERITY.warning,
        text: 'Your credentials were saved, but could not be linked to an existing connection. Contact your administrator to enable this integration for your firm.'
      })
    } else {
      setDomainMessage(domain, null)
    }
  }, [setDomainMessage])

  const requestDisconnect = useCallback((token) => {
    setDisconnectTarget(token)
  }, [])

  const cancelDisconnect = useCallback(() => {
    if (revoking) return
    setDisconnectTarget(null)
  }, [revoking])

  const confirmDisconnect = useCallback(async () => {
    const token = disconnectTarget
    if (!token) return
    try {
      setDomainMessage(token.domain, null)
      await revokeToken({
        userTokenId: token.userTokenId,
        domain: token.domain
      })
      setDisconnectTarget(null)
    } catch (err) {
      setDomainMessage(token.domain, {
        severity: ALERT_SEVERITY.error,
        text: decipherError(err) || 'Failed to disconnect. Please try again.'
      })
      setDisconnectTarget(null)
    }
  }, [disconnectTarget, revokeToken, setDomainMessage])

  if (exposedLoading || tokensLoading) {
    return <Loading />
  }

  if (exposedIsError || tokensIsError) {
    return (
      <Alert severity={ALERT_SEVERITY.error} variant={ALERT_VARIANT.standard}>
        {decipherError(exposedError || tokensError) || 'We couldn\'t load your integrations right now. Please refresh the page or try again in a moment.'}
      </Alert>
    )
  }

  if (!domainGroups.length) {
    return (
      <div className='font-size-sm text-neutral-500'>
        No integrations are available for connection. Contact your administrator.
      </div>
    )
  }

  return (
    <>
      <Grid container spacing={2}>
        {domainGroups.map(group => {
          const message = domainMessages[group.domain]
          const token = group.token
          const requiresReauth = !!token?.requiresReauth
          const canReconnect = requiresReauth && !!group.authInputs
          const status = statusFor(token)
          return (
            <Grid item xs={12} md={6} key={group.domain}>
              <div className='flex flex-col gap-sm p-md' style={DOMAIN_CARD_STYLE}>
                <div className='flex items-center justify-between gap-md'>
                  <div className='flex flex-col gap-xs'>
                    <span className='font-size-md font-bold' title={group.domain}>{group.domainName}</span>
                    <span className={status.className} style={status.style}>{status.label}</span>
                  </div>
                  <div className='flex gap-sm'>
                    {canReconnect ? (
                      <SydButton
                        variant='primary'
                        size='sm'
                        onClick={() => handleConnect(group.domain, group.authInputs, group.domainName)}
                      >
                        Reconnect
                      </SydButton>
                    ) : null}
                    {token ? (
                      <SydButton
                        variant='outline'
                        size='sm'
                        onClick={() => requestDisconnect(token)}
                        disabled={revoking}
                      >
                        Disconnect
                      </SydButton>
                    ) : group.authInputs ? (
                      <SydButton
                        variant='primary'
                        size='sm'
                        onClick={() => handleConnect(group.domain, group.authInputs, group.domainName)}
                      >
                        Connect
                      </SydButton>
                    ) : null}
                  </div>
                </div>
                {requiresReauth ? (
                  <Alert severity={ALERT_SEVERITY.error} variant={ALERT_VARIANT.standard}>
                    Your stored credentials for {group.domainName} are no longer valid. Reconnect to restore access for any smart components powered by this integration.
                  </Alert>
                ) : null}
                {message ? (
                  <Alert severity={message.severity} variant={ALERT_VARIANT.standard}>
                    {message.text}
                  </Alert>
                ) : null}
              </div>
            </Grid>
          )
        })}
      </Grid>
      <AuthDialog ref={authDialogRef} onComplete={handleAuthComplete} />
      <SydModal
        open={!!disconnectTarget}
        onClose={cancelDisconnect}
        title='Disconnect integration'
        size='small'
      >
        <div className='flex flex-col gap-md'>
          <div className='font-size-sm mb-4'>
            {disconnectTarget
              ? `Are you sure you want to disconnect your ${disconnectTarget.domainName || disconnectTarget.domain} integration? You'll need to provide your credentials again to reconnect`
              : ''}
          </div>
          <SydModalActions>
            <SydButton variant='outline' onClick={cancelDisconnect} disabled={revoking}>
              Cancel
            </SydButton>
            <SydButton variant='primary' priority='warning' onClick={confirmDisconnect} processing={revoking}>
              Disconnect
            </SydButton>
          </SydModalActions>
        </div>
      </SydModal>
    </>
  )
}

export default ConnectedIntegrations
